// Works out what an engagement costs the user right now: redeem from balance, or pay for
// the gap. Kept pure and separate from the UI because this is where a wrong number becomes
// a wrong charge.

import { toUSD } from './expertHours';

export type EngagePaymentTarget = { hours: number } | { sku: string };

export interface EngageQuote {
    /** Hours the engagement will consume once it opens. */
    requiredHours: number;
    /** Hours the user is short, and therefore has to buy. */
    shortfallHours: number;
    needsPayment: boolean;
    /** What to send to the payment endpoint, or null when redeeming outright. */
    paymentTarget: EngagePaymentTarget | null;
    /** Dollars charged now. Zero when the balance already covers it. */
    chargeUSD: number;
}

export interface QuoteInput {
    requiredHours: number;
    balanceHours: number;
    /** Fixed-price service SKU, when the service has one. */
    sku?: string;
    /** The SKU's headline price, used when buying the service outright. */
    skuUSD?: number;
    /** Never let balanceHours offset the cost — always charge the full SKU price. For a flat
     * per-project fee (a GitHub import's migration) that must not be discounted by whatever
     * general architect-hours balance the user happens to already be sitting on. Requires sku
     * and skuUSD; without them this has no effect. */
    ignoreBalance?: boolean;
}

export function quoteEngagement({
    requiredHours,
    balanceHours,
    sku,
    skuUSD,
    ignoreBalance,
}: QuoteInput): EngageQuote {
    if (ignoreBalance && sku && skuUSD !== undefined) {
        return {
            requiredHours,
            shortfallHours: requiredHours,
            needsPayment: true,
            paymentTarget: { sku },
            chargeUSD: skuUSD,
        };
    }

    const available = Math.max(0, Math.floor(balanceHours));
    const shortfallHours = Math.max(0, requiredHours - available);

    if (shortfallHours <= 0) {
        return { requiredHours, shortfallHours: 0, needsPayment: false, paymentTarget: null, chargeUSD: 0 };
    }

    // Buying the whole service with no balance to offset uses its SKU, so the user is
    // charged the headline price and the receipt names the service.
    if (sku && skuUSD !== undefined && shortfallHours === requiredHours) {
        return {
            requiredHours,
            shortfallHours,
            needsPayment: true,
            paymentTarget: { sku },
            chargeUSD: skuUSD,
        };
    }

    // Part of the cost is already covered, so only the gap is bought, at the hourly rate.
    return {
        requiredHours,
        shortfallHours,
        needsPayment: true,
        paymentTarget: { hours: shortfallHours },
        chargeUSD: toUSD(shortfallHours),
    };
}
